import { computed, ref } from "vue";
import { AuthenticationStepType, authenticationFlows } from "../models/authentication-flows";
import { flowDataLayerService, restrictedAuthenticationService } from "../services/flows-services";
import useAuthenticationData, { authenticationData } from "./use-authentication-data";

const { clearAuthenticationErrors } = useAuthenticationData();

const authenticationFlowIndex = ref(0);
const authenticationFlow = ref(authenticationFlows.none);
export const authenticationStep = computed(() => authenticationFlow.value[authenticationFlowIndex.value]);

const containsFlowState = (state: AuthenticationStepType) => authenticationFlow.value.includes(state);

const nextFlowStep = () => {
    if (authenticationFlowIndex.value < authenticationFlow.value.length - 1) {
        authenticationFlowIndex.value++;

        flowDataLayerService(authenticationStep.value);
    }
};

const prevFlowStep = () => {
    if (authenticationStep.value == "LoginWelcome" || authenticationFlowIndex.value <= 0) {
        return;
    }

    authenticationFlowIndex.value--;
};

const setFlow = (newFlow: AuthenticationStepType[], stepName?: AuthenticationStepType) => {
    authenticationFlow.value = newFlow;

    clearAuthenticationErrors();

    let newIndex = 0;
    if (stepName) {
        newIndex = authenticationFlow.value.indexOf(stepName);

        if (newIndex < 0) {
            newIndex = 0;
        }
    }
    authenticationFlowIndex.value = newIndex;

    flowDataLayerService(authenticationStep.value);
};

/**
 *  Close all account modals
 */
const closeAccountModal = (): void => {
    setFlow(authenticationFlows.none);
};

/**
 *  Open login modal
 */
const openLoginModal = (): boolean => {
    setFlow(authenticationFlows.login);
    return true;
};

/**
 *  Open login modal. If username is'nt known, open in the first step.
 * @returns true if second step is open
 */
const openLoginPassModal = (): boolean => {
    if (authenticationData.userName) {
        setFlow(authenticationFlows.login, "LoginPass");
        return true;
    } else {
        openLoginModal();
        return false;
    }
};

/**
 *  Open registration modal
 *  @returns True, if modal is open
 */
const openRegistrationModal = (): boolean => {
    if (restrictedAuthenticationService(authenticationData.userName)) {
        return false;
    }

    setFlow(authenticationFlows.registration);
    return true;
};

/**
 *  Open registration modal with 'new password' flow
 *  @returns True, if modal is open
 */
const openRegPasswordModal = (): boolean => {
    if (restrictedAuthenticationService(authenticationData.userName)) {
        return false;
    }

    setFlow(authenticationFlows.regPassword);
    return true;
};

/**
 *  Open registration modal with 'verify phone' flow
 *  @returns True, if modal is open
 */
const openVerifyPhoneModal = (): boolean => {
    if (restrictedAuthenticationService(authenticationData.userName)) {
        return false;
    }

    setFlow(authenticationFlows.verifyPhone);
    return true;
};

export default function useAuthenticationFlows() {
    return {
        containsFlowState,
        nextFlowStep,
        prevFlowStep,
        closeAccountModal,
        openLoginModal,
        openLoginPassModal,
        openRegistrationModal,
        openRegPasswordModal,
        openVerifyPhoneModal,
    };
}
